import { useSelector } from '@/api/state';
import { useRegion } from '@/api/rest/users/useRegion';
import { useTranslation } from '@/i18n';
import BulkCsvErrors from '@/ui/components/bulkUploadDownloadModals/BulkCsvErrors';
import BulkCsvWarnings from '@/ui/components/bulkUploadDownloadModals/BulkCsvWarnings';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { useHashFragment } from '@/utils/customHooks';
import { Button, Modal, H1 } from '@payaca/ambrosia';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import QuickActionsTableCell from '@payaca/components/quickActionsTableCell/QuickActionsTableCell';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import {
  confirmContinueProcessing,
  getCsvUploads,
  getFileDownloadUrl,
} from '@payaca/store/csvUpload/csvUploadActions';
import {
  CsvUpload,
  CsvUploadDataType,
  CsvUploadStatus,
} from '@payaca/types/csvUploadTypes';
import moment from 'moment-timezone';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { downloadFile } from '@/utils/download-file';
import UntitledIcon from '@payaca/untitled-icons';
import './CsvUploadsPage.sass';

type Props = {
  //
};

const POLLING_INTERVAL_MILLIS = 3000;

const CsvUploadsPage: FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const pollCsvUploads = () => {
      dispatch(getCsvUploads.request());
    };

    pollCsvUploads();

    const interval = setInterval(() => {
      pollCsvUploads();
    }, POLLING_INTERVAL_MILLIS);

    return () => {
      clearInterval(interval);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const csvUploads = useSelector((state) => {
    return state.csvUpload.store || {};
  });

  const sortedCsvUploads = useMemo(
    () =>
      Object.values(csvUploads).sort(
        (a, b) => b.createdAt?.localeCompare(a?.createdAt) ?? 0
      ),
    [csvUploads]
  );

  const translate = useTranslation();
  const region = useRegion();
  const shortDateRegionalFormat = useMemo(
    () =>
      getInternationalMomentDateFormatByRegion(DateFormats.TIMESTAMP, region),
    [region]
  );

  const statusIndicators: Record<CsvUploadStatus, React.ReactNode> = useMemo(
    () => ({
      created: <MiniLoader />,
      uploading: <MiniLoader />,
      uploadAborted: (
        <UntitledIcon name="info-circle.3" className="text-yellow-800" />
      ),
      uploadFailed: (
        <UntitledIcon name="info-octagon.3" className="text-red-800" />
      ),
      uploadComplete: <MiniLoader />,
      received: <MiniLoader />,
      validating: <MiniLoader />,
      pendingConfirmation: (
        <UntitledIcon name="info-circle.3" className="text-yellow-800" />
      ),
      validationFailed: (
        <UntitledIcon name="info-octagon.3" className="text-red-800" />
      ),
      validationComplete: <MiniLoader />,
      processing: <MiniLoader />,
      processingFailed: (
        <UntitledIcon name="info-octagon.3" className="text-red-800" />
      ),
      complete: (
        <UntitledIcon name="check-circle.3" className="text-green-800" />
      ),
    }),
    []
  );

  const [selectedCsvUpload, setSelectedCsvUpload] =
    useState<CsvUpload<CsvUploadDataType> | null>(null);
  const [showCsvIssuesModal, toggleCsvIssuesModal] = useHashFragment('#issues');

  return (
    <>
      <Helmet title={translate('pages.csvUploads.title')}></Helmet>
      <AuthenticatedPageWrapper className="csv-uploads-page">
        <div className="px-8 py-4">
          <H1>{translate('pages.csvUploads.title')}</H1>
        </div>
        <div className="main-container">
          <p>
            <Link to={`/customers#bulk-upload`}>Upload customers</Link>
          </p>
          <p>
            <Link to={`/items#bulk-upload`}>Upload new items</Link> /{' '}
            <Link to={`/items#bulk-update`}>Update existing items</Link>
          </p>
          {/* Uncomment once generic materials import is implemented */}
          {/*
          <p>
            <Link to={`/materials#bulk-upload`}>Upload new materials</Link> /{' '}
            <Link to={`/materials#bulk-update`}>Update existing materials</Link>
          </p>
          */}
          <p>
            To upload/update materials for a particular supplier, go to their
            materials list via the <Link to={`/suppliers`}>suppliers page</Link>
            .
          </p>
          {sortedCsvUploads.length === 0 ? (
            <p>You haven&apos;t uploaded any CSV files yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Data type</th>
                  <th colSpan={2}>Status</th>
                  <th>Created at</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sortedCsvUploads.map((csvUpload) => {
                  const quickActions = [
                    (csvUpload.errors || csvUpload.warnings) &&
                      csvUpload.status !== 'complete' && {
                        actionName: 'View issues',
                        actionBehaviour: () => {
                          setSelectedCsvUpload(csvUpload);
                          toggleCsvIssuesModal();
                        },
                      },
                  ];
                  return (
                    <tr key={csvUpload.id}>
                      <td>
                        <Button
                          variant="link"
                          onClick={async () => {
                            dispatch(
                              getFileDownloadUrl.request(
                                csvUpload.id,
                                (url) => {
                                  downloadFile(url, csvUpload.fileName);
                                }
                              )
                            );
                          }}
                        >
                          {csvUpload.fileName}
                        </Button>
                      </td>
                      <td>
                        {translate(
                          `csvUploads.dataTypes.${csvUpload.dataType}`
                        )}
                      </td>
                      <td align="center">
                        {statusIndicators[csvUpload.status]}
                      </td>
                      <td>
                        {translate(`csvUploads.statuses.${csvUpload.status}`)}
                      </td>
                      <td>
                        {moment(csvUpload.createdAt).format(
                          shortDateRegionalFormat
                        )}
                      </td>
                      <QuickActionsTableCell
                        recordId={csvUpload.id}
                        quickActions={quickActions.filter(Boolean) as any}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <Modal
          isOpen={showCsvIssuesModal}
          title="CSV upload issues"
          onClose={toggleCsvIssuesModal}
          className="bulk-update-modal"
        >
          <Modal.Body>
            {/* Upload error  */}
            {selectedCsvUpload?.errors && (
              <BulkCsvErrors uploadCsvError={selectedCsvUpload?.errors || []} />
            )}
            {selectedCsvUpload?.warnings && (
              <BulkCsvWarnings
                uploadCsvWarning={selectedCsvUpload?.warnings || []}
                onConfirmContinueUpload={() => {
                  dispatch(
                    confirmContinueProcessing.request(selectedCsvUpload.id)
                  );
                  toggleCsvIssuesModal();
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default CsvUploadsPage;
